/**
 * Level-up move resolver via @pkmn/data.
 *
 * Replaces Phase 9 M2's hardcoded `SPECIES_DEFAULTS` movesets in
 * `battle/teamFromParty.ts`. For a given species + level, returns the last
 * 4 moves at-or-below that level from the species's Gen-3 learnset — the
 * same set the cartridge would auto-fill on level-up.
 *
 * The dex lives in a lazy chunk (~5 MB), so this stays async to participate
 * in the same `loadGen3` cache the battle scene uses. Top-of-module imports
 * of `@pkmn/data` are deliberately avoided; the bundle gate (3 MB initial)
 * depends on it.
 */
import { loadGen3 } from './dex';

const FALLBACK_MOVES = ['tackle'] as const;

/**
 * Returns up to 4 moves a species would know via level-up at the given
 * level in Gen 3. Falls back to ['tackle'] on any error (missing species,
 * dex chunk failed to load, etc.) so battle setup never throws.
 */
export async function getMovesAtLevel(species: string, level: number): Promise<string[]> {
  try {
    const gen3 = await loadGen3();
    const learnset = await gen3.learnsets.get(species);

    if (!learnset || !learnset.learnset) {
      console.warn(`levelUpMoves: no learnset for species '${species}'`);
      return [...FALLBACK_MOVES];
    }

    // Each entry's value is an array of MoveSource strings like '3L1', '3M',
    // '3T', '3E', '4L7'. We only care about the gen-3 level-up entries
    // ('3L<n>'); ignore TMs/HMs/tutors/breeding/other gens. A move can have
    // multiple sources; we want the *earliest* level it's learned.
    const learned: { move: string; level: number }[] = [];
    for (const [moveId, sources] of Object.entries(learnset.learnset)) {
      let earliestGen3Level: number | null = null;
      for (const src of sources) {
        // Match strings like '3L1', '3L7', '3L42'. The first char is the
        // generation; 'L' marks level-up; the rest is the level.
        if (src.length < 3 || src[0] !== '3' || src[1] !== 'L') continue;
        const lvlStr = src.slice(2);
        const lvl = Number.parseInt(lvlStr, 10);
        if (!Number.isFinite(lvl)) continue;
        if (earliestGen3Level === null || lvl < earliestGen3Level) {
          earliestGen3Level = lvl;
        }
      }
      if (earliestGen3Level !== null && earliestGen3Level <= level) {
        learned.push({ move: moveId, level: earliestGen3Level });
      }
    }

    if (learned.length === 0) {
      // Species exists but learns nothing by `level` — should be rare (most
      // species learn at least one level-1 move) but possible for edge data.
      return [...FALLBACK_MOVES];
    }

    // Ascending by learn level, tie-break by move id for stable ordering.
    learned.sort((a, b) => a.level - b.level || a.move.localeCompare(b.move));

    // Cartridge behavior: the *last* 4 moves at-or-below the level — i.e.
    // the most recently learned ones overwrite earlier slots.
    return learned.slice(-4).map((entry) => entry.move);
  } catch (err) {
    console.warn(`levelUpMoves: failed to resolve moves for '${species}' @ L${level}:`, err);
    return [...FALLBACK_MOVES];
  }
}
